import React, { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useServicePlanStyles } from "../style";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = ["M", "T", "W", "Th", "F", "Sa", "Su"];

const CODE_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
];

const CODE_LABELS = {
  0: "Independent",
  1: "Supervision",
  2: "Limited Assistance",
  3: "Extensive Assistance",
  4: "Totally Dependent",
};

const CODE_COLORS = {
  0: "#4CAF50",
  1: "#009688",
  2: "#5C6BC0",
  3: "#F59E0B",
  4: "#EF4444",
};

const allDaysTrue = () =>
  DAY_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {});

const makeRow = (activity = "New Activity") => ({
  _tempId: `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  activity,
  code: 0,
  days: allDaysTrue(),
});

const AdlInstructionsStep = ({
  data,
  onChange,
  checks = [],
  taskTypeDetails,
  view,
  enabled,
  onToggle,
}) => {
  const { classes } = useServicePlanStyles();
  const [rows, setRows] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  // Track manually removed activity names so they don't re-appear
  const [removedNames, setRemovedNames] = useState(
    () => new Set(data?.removedNames || [])
  );

  // Build a map of fieldId → fieldName from taskTypeDetails
  const fieldIdToName = useMemo(() => {
    const map = {};
    if (!taskTypeDetails?.subTasks) return map;
    for (const sub of taskTypeDetails.subTasks) {
      if (!sub?.fields) continue;
      for (const field of sub.fields) {
        if (field?._id && field?.name) {
          map[field._id] = field.name;
        }
      }
    }
    return map;
  }, [taskTypeDetails]);

  // Derive actual checked field names (deduplicated), excluding removed ones
  const selectedItems = useMemo(() => {
    if (!checks?.length) return [];
    const names = [];
    const seen = new Set();
    for (const c of checks) {
      const name = fieldIdToName[c.field];
      if (
        name &&
        !seen.has(name.toLowerCase()) &&
        !removedNames.has(name.toLowerCase())
      ) {
        seen.add(name.toLowerCase());
        names.push(name);
      }
    }
    return names;
  }, [checks, fieldIdToName, removedNames]);

  // Initialize rows from saved data or from selected items
  useEffect(() => {
    if (!enabled) return;
    if (data?.rows?.length > 0 && !hasInitialized) {
      setRows(
        data.rows.map((r) => ({
          ...r,
          _tempId:
            r._tempId ||
            r._id ||
            `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        }))
      );
      setHasInitialized(true);
    } else if (!hasInitialized && selectedItems.length > 0) {
      setRows(selectedItems.map((name) => makeRow(name)));
      setHasInitialized(true);
    }
  }, [enabled, data, selectedItems, hasInitialized]);

  // Sync: add new items not already in rows (and not removed)
  useEffect(() => {
    if (!enabled || !hasInitialized) return;
    const existingNames = new Set(rows.map((r) => r.activity?.toLowerCase()));
    const newItems = selectedItems.filter(
      (name) => !existingNames.has(name.toLowerCase())
    );
    if (newItems.length > 0) {
      setRows((prev) => [...prev, ...newItems.map((n) => makeRow(n))]);
    }
  }, [selectedItems, enabled, hasInitialized]);

  // Propagate changes to parent
  useEffect(() => {
    if (!enabled) return;
    const cleaned = rows.map(({ _tempId, ...rest }) => rest);
    onChange({
      rows: cleaned,
      enabled: true,
      removedNames: Array.from(removedNames),
    });
  }, [rows, enabled, removedNames]);

  const updateRow = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );
  };

  const toggleDay = (idx, dayKey) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === idx
          ? { ...r, days: { ...r.days, [dayKey]: !r.days[dayKey] } }
          : r
      )
    );
  };

  const removeRow = (idx) => {
    const removedActivity = rows[idx]?.activity;
    if (removedActivity) {
      setRemovedNames((prev) => new Set([...prev, removedActivity.toLowerCase()]));
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    setRows((prev) => [...prev, makeRow()]);
  };

  if (!enabled) {
    return (
      <Box
        mt="lg"
        p="md"
        style={{
          background: "var(--app-tint-purple)",
          borderRadius: 10,
          border: "1px solid var(--app-border)",
        }}
      >
        <Flex align="center" gap="md">
          <Text fw={600} size="sm" color="var(--app-text)">
            Add ADL Instructions
          </Text>
          <Switch
            checked={false}
            onChange={(e) => onToggle(e.currentTarget.checked)}
            color="violet"
            size="sm"
          />
        </Flex>
        <Text size="xs" color="var(--app-text-muted)" mt={4}>
          Enable to add ADL instruction codes and day schedules. Items checked
          above will auto-populate.
        </Text>
      </Box>
    );
  }

  return (
    <Box
      mt="lg"
      p="md"
      style={{
        background: "var(--app-tint-purple)",
        borderRadius: 10,
        border: "1px solid var(--app-border)",
      }}
    >
      <Flex align="center" justify="space-between" mb="sm">
        <Flex align="center" gap="md">
          <Text fw={600} size="sm" color="var(--app-text)">
            ADL Instructions
          </Text>
          <Switch
            checked={true}
            onChange={(e) => onToggle(e.currentTarget.checked)}
            color="violet"
            size="sm"
          />
        </Flex>
      </Flex>

      <Box style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
            fontFamily: "Inter, sans-serif",
          }}
        >
          <thead>
            <tr style={{ background: "var(--app-tint-purple)" }}>
              <th
                style={{
                  textAlign: "left",
                  padding: "6px 8px",
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "var(--app-text-muted)",
                  borderBottom: "2px solid var(--app-border)",
                  minWidth: 160,
                }}
              >
                Activity
              </th>
              <th
                style={{
                  textAlign: "center",
                  padding: "6px 4px",
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: "uppercase",
                  color: "var(--app-text-muted)",
                  borderBottom: "2px solid var(--app-border)",
                  minWidth: 50,
                }}
              >
                Code
              </th>
              {DAY_LABELS.map((label) => (
                <th
                  key={label}
                  style={{
                    textAlign: "center",
                    padding: "6px 2px",
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: "uppercase",
                    color: "var(--app-text-muted)",
                    borderBottom: "2px solid var(--app-border)",
                    minWidth: 32,
                  }}
                >
                  {label}
                </th>
              ))}
              {!view && (
                <th
                  style={{
                    width: 36,
                    borderBottom: "2px solid var(--app-border)",
                  }}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr
                key={row._tempId || row._id || idx}
                style={{
                  background: idx % 2 === 0 ? "var(--app-surface)" : "var(--app-surface-2)",
                }}
              >
                {/* Activity — editable with visible border */}
                <td
                  style={{
                    padding: "4px 8px",
                    borderBottom: "1px solid var(--app-border)",
                  }}
                >
                  {view ? (
                    <Text size="sm" fw={500}>
                      {row.activity}
                    </Text>
                  ) : (
                    <TextInput
                      value={row.activity}
                      onChange={(e) =>
                        updateRow(idx, "activity", e.currentTarget.value)
                      }
                      variant="default"
                      size="xs"
                      styles={{
                        input: {
                          fontWeight: 500,
                          fontSize: 13,
                          padding: "4px 8px",
                          minHeight: 30,
                          background: "var(--app-surface)",
                          border: "1px solid var(--app-border)",
                          borderRadius: 4,
                        },
                      }}
                    />
                  )}
                </td>

                {/* Code — white dropdown, number only */}
                <td
                  style={{
                    textAlign: "center",
                    padding: "4px 4px",
                    borderBottom: "1px solid var(--app-border)",
                  }}
                >
                  {view ? (
                    <Text size="sm" fw={600} ta="center">
                      {row.code ?? 0}
                    </Text>
                  ) : (
                    <Select
                      value={String(row.code ?? 0)}
                      onChange={(v) => updateRow(idx, "code", parseInt(v) || 0)}
                      data={CODE_OPTIONS}
                      size="xs"
                      styles={{
                        input: {
                          minHeight: 30,
                          fontSize: 13,
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "0 8px",
                          background: "var(--app-surface)",
                          border: "1px solid var(--app-border)",
                          borderRadius: 4,
                          width: 50,
                          margin: "0 auto",
                        },
                        item: { fontSize: 13, fontWeight: 600 },
                      }}
                    />
                  )}
                </td>

                {/* Day checkboxes */}
                {DAY_KEYS.map((dk) => (
                  <td
                    key={dk}
                    style={{
                      textAlign: "center",
                      padding: "4px 2px",
                      borderBottom: "1px solid var(--app-border)",
                      cursor: view ? "default" : "pointer",
                    }}
                    onClick={view ? undefined : () => toggleDay(idx, dk)}
                  >
                    <Box
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: row.days?.[dk] ? "var(--app-tint-purple)" : "var(--app-surface-2)",
                        border: row.days?.[dk]
                          ? "1.5px solid #A78BFA"
                          : "1.5px solid var(--app-border)",
                        color: row.days?.[dk] ? "#FFF" : "var(--app-text-muted)",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {row.days?.[dk] ? "✓" : ""}
                    </Box>
                  </td>
                ))}

                {/* Delete */}
                {!view && (
                  <td
                    style={{
                      textAlign: "center",
                      padding: "4px 2px",
                      borderBottom: "1px solid var(--app-border)",
                    }}
                  >
                    <Tooltip label="Remove">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        onClick={() => removeRow(idx)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {!view && (
          <Button
            variant="subtle"
            color="violet"
            size="xs"
            leftIcon={<IconPlus size={14} />}
            mt={8}
            onClick={addRow}
          >
            Add activity
          </Button>
        )}
      </Box>

      {/* Code legend */}
      <Flex gap="md" mt={10} wrap="wrap" align="center">
        <Text size="xs" fw={600} color="var(--app-text-muted)">
          Codes:
        </Text>
        {Object.entries(CODE_LABELS).map(([code, label]) => (
          <Flex key={code} align="center" gap={4}>
            <Badge
              size="xs"
              style={{
                background: CODE_COLORS[code],
                color: "#FFF",
                minWidth: 18,
              }}
            >
              {code}
            </Badge>
            <Text size="xs" color="var(--app-text-muted)">
              {label}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

export default AdlInstructionsStep;
